import api from ".";
import { submissionEndpoints } from "./endpoints/submission.endpoints";

export const submissionApis = {
  submitForSchema: async (schemaId, data) => {
    try {
      const res = await api.post(submissionEndpoints.submit(schemaId), data);
      return res.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || "Error submitting form");
    }
  },

  getAllSubmissions: async (params = {}) => {
    try {
      const { sector, schemaId, limit, page } = params;
      const queryParams = new URLSearchParams();
      if (sector) queryParams.append("sector", sector);
      if (schemaId) queryParams.append("schemaId", schemaId);
      if (limit) queryParams.append("limit", limit);
      if (page) queryParams.append("page", page);

      const queryString = queryParams.toString();
      const url = queryString
        ? `${submissionEndpoints.getAll}?${queryString}`
        : submissionEndpoints.getAll;

      const res = await api.get(url);
      return res.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || "Error fetching submissions");
    }
  },

  getSubmissionById: async (id) => {
    try {
      const res = await api.get(submissionEndpoints.getById(id));
      return res.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || "Error fetching submission");
    }
  },

  getSubmissionsBySector: async (sector) => {
    try {
      const encodedSector = encodeURIComponent(sector);
      const res = await api.get(submissionEndpoints.getBySector(encodedSector));
      return Array.isArray(res.data) ? res.data : (res.data?.data || res.data || []);
    } catch (error) {
      throw new Error(error.response?.data?.message || "Error fetching submissions by sector");
    }
  },

  getSubmissionsByReport: async (reportId) => {
    try {
      const res = await api.get(submissionEndpoints.getByReport(reportId));
      return Array.isArray(res.data) ? res.data : (res.data?.data || res.data || []);
    } catch (error) {
      throw new Error(error.response?.data?.message || "Error fetching submissions by report");
    }
  },

  downloadDatapointExcelTemplate: async (reportId) => {
    try {
      const res = await api.get(submissionEndpoints.excelTemplate(reportId), {
        responseType: "blob",
      });
      const contentType = String(res.headers?.["content-type"] || "");
      if (contentType.includes("application/json")) {
        const text = await res.data.text();
        const parsed = JSON.parse(text);
        throw new Error(parsed.message || "Could not download template");
      }
      const disposition = res.headers?.["content-disposition"] || "";
      const match = /filename="?([^"]+)"?/i.exec(disposition);
      const filename = match?.[1] || `si-datapoints-${reportId}.xlsx`;
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      return true;
    } catch (error) {
      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const parsed = JSON.parse(text);
          throw new Error(parsed.message || "Could not download template");
        } catch (inner) {
          if (inner instanceof Error && inner.message !== "Could not download template") {
            throw inner;
          }
        }
      }
      throw new Error(
        error.response?.data?.message || error.message || "Could not download template"
      );
    }
  },

  importDatapointExcel: async (reportId, file, { overwrite = false } = {}) => {
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await api.post(
        `${submissionEndpoints.importExcel(reportId)}?overwrite=${overwrite ? "true" : "false"}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      return res.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || error.message || "Excel import failed"
      );
    }
  },
};

